import {
  ATTACH_KINDS,
  MAX_MOD_DEPTH,
  TRADERS,
  buyPrice,
  getItemDef,
  traderSells,
  type AttachKind,
  type AttachmentItemDef,
  type GridState,
  type Inventory,
  type InventoryOp,
  type ItemInstance,
  type TraderDef,
  type WeaponItemDef,
  type WeaponSlotDef,
} from "@rietz/shared";
import type { OpenContainer } from "../store.js";
import { nestingRoots } from "../inventory/drag.js";
import { detachTargetFor } from "../inventory/modLogic.js";

/**
 * The workbench's PLAN: what the gun would look like, judged against what the
 * player owns and what the valley sells, and the ops that would build it. Pure,
 * so the screen only draws. A plan is keyed by PATH, not by instance — a part
 * nobody has bought yet has no iid, and a path survives every op the assembly
 * runs. `planRows` is `modSlotRows`' shape asked of a hypothetical gun;
 * `planInstance` makes that hypothesis an instance, so the viewer and the stat
 * table read it through the same shared accessors the fired gun does.
 */

/** Path → def id, or null for "leave empty". A path is `optic` for a slot on
 *  the gun, `handguard/grip` for a slot on the part in the handguard slot.
 *  An ABSENT path means "keep whatever the gun wears there". */
export type BuildPlan = Readonly<Record<string, string | null>>;

/** A slot's path under its host's. The gun's own slots are bare slot ids. */
export function slotPath(parent: string | null, slot: AttachKind): string {
  return parent === null ? slot : `${parent}/${slot}`;
}

/** The host's path, or `null` when the host is the gun itself. */
export function parentPath(path: string): string | null {
  const cut = path.lastIndexOf("/");
  return cut < 0 ? null : path.slice(0, cut);
}

/** The plan with one path decided, as a new object — the store holds it. */
export function withPick(plan: BuildPlan, path: string, defId: string | null): BuildPlan {
  return { ...plan, [path]: defId };
}

/** What the plan says about `path`, `undefined` when it says nothing. Own
 *  property only: a path like `constructor` must not answer off the prototype. */
function picked(plan: BuildPlan, path: string): string | null | undefined {
  return Object.prototype.hasOwnProperty.call(plan, path) ? plan[path] : undefined;
}

/**
 * Where a planned part stands: on the gun already (`fitted`), lying in a grid
 * (`owned`), on a shelf (`buy`), nowhere at all (`unavailable`), or the slot is
 * to stay empty.
 */
export type PlanStatus = "fitted" | "owned" | "buy" | "unavailable" | "empty";

/** One mounting point of the PLANNED gun. */
export interface PlanRow {
  path: string;
  slot: WeaponSlotDef;
  depth: number;
  vital: boolean;
  /** The path — unique by construction, unlike `slot.id`. */
  key: string;
  hostDef: WeaponItemDef | AttachmentItemDef;
  /**
   * The real instance on the gun at the parent path (the gun itself at depth
   * 0), or `null` when the parent is not on the gun yet. This is the `hostIid`
   * an attach op needs.
   */
  hostIid: string | null;
  /** What the gun wears at this path NOW. */
  current: { item: ItemInstance; def: AttachmentItemDef } | null;
  planned: AttachmentItemDef | null;
  /** Planned === current AND every ancestor row is kept. Only a kept row needs
   *  no op — a part under a replaced host comes off with it. */
  keep: boolean;
  status: PlanStatus;
  /** Cheapest asking price over `TRADERS`, or `null` when nobody sells it. */
  price: number | null;
}

/** `fittedMods`' three tests, so a row and the fired gun describe one slot. */
function fittedIn(
  slot: WeaponSlotDef,
  raw: ItemInstance | undefined,
): { item: ItemInstance; def: AttachmentItemDef } | null {
  if (raw === undefined) return null;
  const def = getItemDef(raw.defId);
  if (def === null || def.kind !== "attachment") return null;
  if (def.attachKind !== slot.id) return null;
  if (!slot.accepts.includes(def.id)) return null;
  return { item: raw, def };
}

/** The def `id` names, if this slot would take it — the same whitelist a drop
 *  is judged against, applied to a wish rather than to an instance. */
function acceptedDef(slot: WeaponSlotDef, id: string | null): AttachmentItemDef | null {
  if (id === null) return null;
  const def = getItemDef(id);
  if (def === null || def.kind !== "attachment") return null;
  if (def.attachKind !== slot.id) return null;
  if (!slot.accepts.includes(def.id)) return null;
  return def;
}

/**
 * The planned gun as rows, depth-first in declaration order — `modSlotRows`'
 * fold order asked of the plan. A block grows under the PLANNED host, with the
 * CURRENT host's instance still handed down: a new cover that accepts the old
 * glass keeps it, which is what anybody with the parts on the bench would do.
 * Statuses come after the walk, because "owned" is a pool that empties.
 */
export function planRows(
  def: WeaponItemDef,
  item: ItemInstance,
  plan: BuildPlan,
  inv: Inventory,
  stash: OpenContainer | null,
): PlanRow[] {
  const rows: PlanRow[] = [];

  const walk = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    currentHostItem: ItemInstance | null,
    parent: string | null,
    depth: number,
    parentKeep: boolean,
    hostIid: string | null,
  ): void => {
    for (const slot of hostDef.slots ?? []) {
      const path = slotPath(parent, slot.id);
      const current = fittedIn(slot, currentHostItem?.mods?.[slot.id]);
      const wish = picked(plan, path);
      const planned = acceptedDef(slot, wish === undefined ? current?.def.id ?? null : wish);
      // Two empties count as kept: a slot that was empty and stays empty needs
      // no op either.
      const keep = parentKeep && (planned?.id ?? null) === (current?.def.id ?? null);
      rows.push({
        path,
        slot,
        depth,
        vital: slot.vital === true,
        key: path,
        hostDef,
        hostIid,
        current,
        planned,
        keep,
        status: "empty",
        price: planned === null ? null : cheapestOffer(planned.id)?.price ?? null,
      });
      if (planned === null || depth + 2 > MAX_MOD_DEPTH) continue;
      // The CURRENT instance goes down even under a different planned host, so
      // its children are offered to whatever slots the new host declares. The
      // child's `hostIid` is the REAL one — null while that host is off the gun.
      walk(planned, current?.item ?? null, path, depth + 1, keep, current?.item.iid ?? null);
    }
  };
  walk(def, item, null, 0, true, item.iid);

  const pool = ownedPool(def, item, rows, inv, stash);
  for (const row of rows) {
    const planned = row.planned;
    if (planned === null) {
      row.status = "empty";
      continue;
    }
    if (row.keep) {
      row.status = "fitted";
      continue;
    }
    const have = pool.get(planned.id) ?? 0;
    if (have > 0) {
      pool.set(planned.id, have - 1);
      row.status = "owned";
      continue;
    }
    row.status = row.price === null ? "unavailable" : "buy";
  }
  return rows;
}

/** One part currently on the gun, and where it sits. */
interface CurrentPart {
  path: string;
  slot: WeaponSlotDef;
  /** Depth of the SLOT: 0 for a slot on the gun itself. */
  depth: number;
  item: ItemInstance;
  def: AttachmentItemDef;
}

/** Every part on the gun now, depth-first, with the path each one occupies. */
function currentParts(def: WeaponItemDef, item: ItemInstance): CurrentPart[] {
  const out: CurrentPart[] = [];
  const walk = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance,
    parent: string | null,
    depth: number,
  ): void => {
    for (const slot of hostDef.slots ?? []) {
      const fitted = fittedIn(slot, hostItem.mods?.[slot.id]);
      if (fitted === null) continue;
      const path = slotPath(parent, slot.id);
      out.push({ path, slot, depth, item: fitted.item, def: fitted.def });
      if (depth + 2 > MAX_MOD_DEPTH) continue;
      walk(fitted.def, fitted.item, path, depth + 1);
    }
  };
  walk(def, item, null, 0);
  return out;
}

/**
 * Whether the plan leaves this part exactly where it is. A part whose path has
 * no row at all is NOT kept — the rows cover the PLANNED tree, so a foregrip
 * under a replaced handguard simply falls out of it.
 */
function partKept(rows: readonly PlanRow[], part: CurrentPart): boolean {
  const row = rows.find((r) => r.path === part.path);
  return row !== undefined && row.current?.item.iid === part.item.iid && row.keep;
}

/** Every loose attachment the screen can reach, counted by def — grids and the
 *  boxes in them, at any depth. Equipment slots and fitted parts are not loose. */
function loosePool(inv: Inventory, stash: OpenContainer | null): Map<string, number> {
  const pool = new Map<string, number>();
  const walk = (grid: GridState): void => {
    for (const placed of grid.items) {
      const def = getItemDef(placed.item.defId);
      if (def !== null && def.kind === "attachment") {
        pool.set(def.id, (pool.get(def.id) ?? 0) + 1);
      }
      const contents = placed.item.contents;
      if (contents !== undefined) walk(contents);
    }
  };
  for (const grid of nestingRoots(inv, stash)) walk(grid);
  return pool;
}

/**
 * What the assembly may draw on: the loose parts, plus every part the plan
 * takes OFF the gun. The vacated ones are only loose after the detach phase,
 * but they are as good as owned — nobody should be sent shopping for the scope
 * they are about to unscrew.
 */
function ownedPool(
  def: WeaponItemDef,
  item: ItemInstance,
  rows: readonly PlanRow[],
  inv: Inventory,
  stash: OpenContainer | null,
): Map<string, number> {
  const pool = loosePool(inv, stash);
  for (const part of currentParts(def, item)) {
    if (partKept(rows, part)) continue;
    pool.set(part.def.id, (pool.get(part.def.id) ?? 0) + 1);
  }
  return pool;
}

/**
 * The cheapest counter stocking `defId`, or `null`. Gates are NOT asked here:
 * the bench says what a part costs, and the market says who will hand it over.
 */
export function cheapestOffer(defId: string): { trader: TraderDef; price: number } | null {
  const known = OFFER_CACHE.get(defId);
  if (known !== undefined) return known;
  let best: { trader: TraderDef; price: number } | null = null;
  for (const trader of TRADERS) {
    if (!traderSells(trader, defId)) continue;
    const price = buyPrice(trader, defId);
    if (best === null || price < best.price) best = { trader, price };
  }
  OFFER_CACHE.set(defId, best);
  return best;
}

/** The shelves never change at runtime, and every row of every render asks. */
const OFFER_CACHE = new Map<string, { trader: TraderDef; price: number } | null>();

/** One line of the shopping list: a part, how many the plan wants, and where. */
export interface ShoppingLine {
  def: AttachmentItemDef;
  count: number;
  offer: { trader: TraderDef; price: number } | null;
}

/** What still has to be bought before the plan can be assembled, aggregated by
 *  def in row order. A line with no offer is a plan that cannot be finished. */
export function shoppingList(rows: readonly PlanRow[]): ShoppingLine[] {
  const out: ShoppingLine[] = [];
  const byId = new Map<string, ShoppingLine>();
  for (const row of rows) {
    if (row.status !== "buy" && row.status !== "unavailable") continue;
    const def = row.planned;
    if (def === null) continue;
    const line = byId.get(def.id);
    if (line === undefined) {
      const made: ShoppingLine = { def, count: 1, offer: cheapestOffer(def.id) };
      byId.set(def.id, made);
      out.push(made);
    } else {
      line.count += 1;
    }
  }
  return out;
}

/** Whether the plan asks for anything at all. */
export function planDiffers(rows: readonly PlanRow[]): boolean {
  return rows.some((row) => !row.keep);
}

/**
 * The plan with every path spelled out. An absent path means "whatever the gun
 * wears there", and once the host above it comes off there is nothing left for
 * that to mean — so the assembly spells the plan out ONCE against the gun as it
 * stands, then builds it. Without this a carried-over part (the protector that
 * moves to the new barrel) would be unscrewed and never put back.
 */
export function explicitPlan(
  def: WeaponItemDef,
  item: ItemInstance,
  plan: BuildPlan,
  inv: Inventory,
  stash: OpenContainer | null,
): BuildPlan {
  const out: Record<string, string | null> = {};
  for (const row of planRows(def, item, plan, inv, stash)) out[row.path] = row.planned?.id ?? null;
  return out;
}

/**
 * The gun as the plan would leave it: a synthetic instance for the viewer and
 * the stat table. A part that stays keeps its own instance (rounds, wear and
 * all); a new one is minted against its path, so the same plan always mints the
 * same ids. Key order is `ATTACH_KINDS`, as `weaponWithMod` writes it.
 */
export function planInstance(
  def: WeaponItemDef,
  item: ItemInstance,
  plan: BuildPlan,
): ItemInstance {
  const build = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    currentHostItem: ItemInstance | null,
    parent: string | null,
    depth: number,
  ): { [slotId: string]: ItemInstance } | null => {
    const chosen = new Map<AttachKind, ItemInstance>();
    for (const slot of hostDef.slots ?? []) {
      const path = slotPath(parent, slot.id);
      const current = fittedIn(slot, currentHostItem?.mods?.[slot.id]);
      const wish = picked(plan, path);
      const planned = acceptedDef(slot, wish === undefined ? current?.def.id ?? null : wish);
      if (planned === null) continue;
      const same = current !== null && current.def.id === planned.id;
      const child: ItemInstance = same
        ? { ...current.item }
        : { iid: `plan:${path}`, defId: planned.id, stack: 1 };
      // Whatever the instance carried is re-decided by the recursion below.
      delete child.mods;
      const sub =
        depth + 2 > MAX_MOD_DEPTH ? null : build(planned, current?.item ?? null, path, depth + 1);
      if (sub !== null) child.mods = sub;
      chosen.set(slot.id, child);
    }
    const out: { [slotId: string]: ItemInstance } = {};
    for (const kind of ATTACH_KINDS) {
      const child = chosen.get(kind);
      if (child !== undefined) out[kind] = child;
    }
    return Object.keys(out).length === 0 ? null : out;
  };

  const mods = build(def, item, null, 0);
  const out: ItemInstance = { ...item };
  if (mods === null) delete out.mods;
  else out.mods = mods;
  return out;
}

/** One line of the parts list under a selected slot: everything the slot
 *  accepts, whether the player owns it or not. */
export interface PartOption {
  def: AttachmentItemDef;
  onGun: boolean;
  owned: number;
  offer: { trader: TraderDef; price: number } | null;
  picked: boolean;
}

/**
 * Every part `row`'s slot accepts, in `accepts` order. `owned` counts the loose
 * pool plus the parts other rows are giving up — the same "as good as owned"
 * rule the statuses use, read off the rows, which is what the plan knows about
 * the parts it displaces.
 */
export function partOptions(
  row: PlanRow,
  rows: readonly PlanRow[],
  inv: Inventory,
  stash: OpenContainer | null,
): PartOption[] {
  const pool = loosePool(inv, stash);
  for (const other of rows) {
    const giving = other.current;
    if (giving === null || other.keep) continue;
    pool.set(giving.def.id, (pool.get(giving.def.id) ?? 0) + 1);
  }

  const out: PartOption[] = [];
  for (const id of row.slot.accepts) {
    const def = acceptedDef(row.slot, id);
    if (def === null) continue;
    out.push({
      def,
      onGun: row.current?.def.id === def.id,
      owned: pool.get(def.id) ?? 0,
      offer: cheapestOffer(def.id),
      picked: row.planned?.id === def.id,
    });
  }
  return out;
}

/**
 * One step of the build: an op to apply, "done", or why it cannot go on. The
 * caller applies the op and asks again — the optimistic `tryOp` is synchronous,
 * so a plain loop assembles the gun.
 */
export type AssemblyStep =
  | { t: "op"; op: InventoryOp }
  | { t: "done" }
  | { t: "blocked"; reason: string };

/**
 * The next op, computed against the CURRENT state. Everything the plan
 * displaces comes off first, deepest first (a child must not be carried away
 * inside its host), and only then does anything go on — so a part moving from
 * one host to another is loose by the time it is wanted.
 */
export function nextAssemblyStep(
  def: WeaponItemDef,
  item: ItemInstance,
  inv: Inventory,
  stash: OpenContainer | null,
  plan: BuildPlan,
): AssemblyStep {
  const rows = planRows(def, item, plan, inv, stash);

  // --- Detach. Ties go to the LATER part in walk order; any deepest-first
  // order is correct, this one is deterministic.
  const parts = currentParts(def, item);
  let victim: CurrentPart | null = null;
  for (const part of parts) {
    if (partKept(rows, part)) continue;
    if (victim === null || part.depth >= victim.depth) victim = part;
  }
  if (victim !== null) {
    const parent = parentPath(victim.path);
    const host = parent === null ? null : parts.find((p) => p.path === parent) ?? null;
    // Unreachable: a part at a nested path was found THROUGH its host.
    if (parent !== null && host === null) {
      return { t: "blocked", reason: `Cannot reach the ${victim.def.name}.` };
    }
    const to = detachTargetFor(inv, victim.def, stash);
    if (to === null) {
      return { t: "blocked", reason: `No room in your stash for the ${victim.def.name}.` };
    }
    // Absent `hostIid` IS the gun on the wire — the shape the bench has always
    // sent for a gun-level slot.
    const op: InventoryOp =
      host === null
        ? { t: "detach", weaponIid: item.iid, slot: victim.slot.id, to }
        : { t: "detach", weaponIid: item.iid, slot: victim.slot.id, to, hostIid: host.item.iid };
    return { t: "op", op };
  }

  // --- Attach, in row order, so a host is on the gun before its own slots are
  // asked about.
  for (const row of rows) {
    const planned = row.planned;
    if (planned === null || row.current !== null) continue;
    // Defensive: the parent row comes first, so its part is already on by now.
    if (row.hostIid === null) continue;
    const loose = findLoose(inv, stash, planned.id);
    if (loose === null) return { t: "blocked", reason: `Missing part: ${planned.name}.` };
    const op: InventoryOp =
      row.hostIid === item.iid
        ? { t: "attach", iid: loose.iid, weaponIid: item.iid, slot: row.slot.id }
        : {
            t: "attach",
            iid: loose.iid,
            weaponIid: item.iid,
            slot: row.slot.id,
            hostIid: row.hostIid,
          };
    return { t: "op", op };
  }

  return { t: "done" };
}

/** The first loose instance of `defId` anywhere the screen can reach. */
function findLoose(
  inv: Inventory,
  stash: OpenContainer | null,
  defId: string,
): ItemInstance | null {
  const walk = (grid: GridState): ItemInstance | null => {
    for (const placed of grid.items) {
      if (placed.item.defId === defId) return placed.item;
      const contents = placed.item.contents;
      if (contents === undefined) continue;
      const deeper = walk(contents);
      if (deeper !== null) return deeper;
    }
    return null;
  };
  for (const grid of nestingRoots(inv, stash)) {
    const hit = walk(grid);
    if (hit !== null) return hit;
  }
  return null;
}
